import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams, useNavigate } from "react-router-dom";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n from "./i18n/i18n";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";

// Pages
import Home from "./pages/Home";
import Services from "./pages/Services";
import ZulassungsfreieTaetigkeiten from "./pages/ZulassungsfreieTaetigkeiten";
import UeberMich from "./pages/UeberMich";
import Arbeitsweise from "./pages/Arbeitsweise";
import Kontakt from "./pages/Kontakt";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import CookieRichtlinie from "./pages/CookieRichtlinie";
import Disclaimer from "./pages/Disclaimer";
import TrockenbauDetail from "./pages/TrockenbauDetail";
import BodenbelaegeDetail from "./pages/BodenbelaegeDetail";
import ReparaturenDetail from "./pages/ReparaturenDetail";
import OekologischeMaterialienDetail from "./pages/OekologischeMaterialienDetail";
import ReviewPage from "./pages/ReviewPage";
import AdminReviews from "./pages/AdminReviews";

const SUPPORTED_LANGS = ['de', 'en', 'it', 'es'];

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Language wrapper that syncs URL with i18n
function LanguageWrapper({ children }) {
  const { lang } = useParams();
  const navigate = useNavigate();
  const { i18n: i18nInstance } = useTranslation();

  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang)) {
      if (i18nInstance.language !== lang) {
        i18nInstance.changeLanguage(lang);
        localStorage.setItem('i18nextLng', lang);
      }
    }
  }, [lang, i18nInstance]);

  return children;
}

// Main routes with language prefix
function LocalizedRoutes() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to="/de" replace />} />
      
      {/* Language-prefixed routes */}
      <Route path="/:lang/*" element={
        <LanguageWrapper>
          <Routes>
            <Route index element={<Home />} />
            <Route path="leistungen" element={<Services />} />
            <Route path="services" element={<Services />} />
            <Route path="servizi" element={<Services />} />
            <Route path="servicios" element={<Services />} />
            <Route path="trockenbau" element={<TrockenbauDetail />} />
            <Route path="drywall-services" element={<TrockenbauDetail />} />
            <Route path="cartongesso" element={<TrockenbauDetail />} />
            <Route path="tabiqueria-seca" element={<TrockenbauDetail />} />
            <Route path="bodenbelaege" element={<BodenbelaegeDetail />} />
            <Route path="flooring-services" element={<BodenbelaegeDetail />} />
            <Route path="pavimenti" element={<BodenbelaegeDetail />} />
            <Route path="suelos" element={<BodenbelaegeDetail />} />
            <Route path="kleinreparaturen" element={<ReparaturenDetail />} />
            <Route path="minor-repairs" element={<ReparaturenDetail />} />
            <Route path="piccole-riparazioni" element={<ReparaturenDetail />} />
            <Route path="pequenas-reparaciones" element={<ReparaturenDetail />} />
            <Route path="oekologische-materialien" element={<OekologischeMaterialienDetail />} />
            <Route path="ecological-materials" element={<OekologischeMaterialienDetail />} />
            <Route path="materiali-ecologici" element={<OekologischeMaterialienDetail />} />
            <Route path="materiales-ecologicos" element={<OekologischeMaterialienDetail />} />
            <Route path="zulassungsfreie-taetigkeiten" element={<ZulassungsfreieTaetigkeiten />} />
            <Route path="license-free" element={<ZulassungsfreieTaetigkeiten />} />
            <Route path="attivita-senza-licenza" element={<ZulassungsfreieTaetigkeiten />} />
            <Route path="actividades-sin-licencia" element={<ZulassungsfreieTaetigkeiten />} />
            <Route path="ueber-mich" element={<UeberMich />} />
            <Route path="about" element={<UeberMich />} />
            <Route path="chi-sono" element={<UeberMich />} />
            <Route path="sobre-mi" element={<UeberMich />} />
            <Route path="arbeitsweise" element={<Arbeitsweise />} />
            <Route path="work-process" element={<Arbeitsweise />} />
            <Route path="metodo" element={<Arbeitsweise />} />
            <Route path="proceso" element={<Arbeitsweise />} />
            <Route path="kontakt" element={<Kontakt />} />
            <Route path="contact" element={<Kontakt />} />
            <Route path="contatti" element={<Kontakt />} />
            <Route path="contacto" element={<Kontakt />} />
            <Route path="impressum" element={<Impressum />} />
            <Route path="legal-notice" element={<Impressum />} />
            <Route path="note-legali" element={<Impressum />} />
            <Route path="aviso-legal" element={<Impressum />} />
            <Route path="datenschutz" element={<Datenschutz />} />
            <Route path="privacy" element={<Datenschutz />} />
            <Route path="privacy-policy" element={<Datenschutz />} />
            <Route path="politica-privacidad" element={<Datenschutz />} />
            <Route path="cookie-richtlinie" element={<CookieRichtlinie />} />
            <Route path="cookie-policy" element={<CookieRichtlinie />} />
            <Route path="politica-cookie" element={<CookieRichtlinie />} />
            <Route path="politica-cookies" element={<CookieRichtlinie />} />
            <Route path="disclaimer" element={<Disclaimer />} />
            <Route path="haftungsausschluss" element={<Disclaimer />} />
            <Route path="esclusione-responsabilita" element={<Disclaimer />} />
            <Route path="exencion-responsabilidad" element={<Disclaimer />} />
            <Route path="kundenbewertung" element={<ReviewPage />} />
            <Route path="customer-review" element={<ReviewPage />} />
            <Route path="recensione" element={<ReviewPage />} />
            <Route path="resena" element={<ReviewPage />} />
            <Route path="admin/reviews" element={<AdminReviews />} />
            <Route path="*" element={<Navigate to="" replace />} />
          </Routes>
        </LanguageWrapper>
      } />
    </Routes>
  );
}

function AppContent() {
  const [cookiesAccepted, setCookiesAccepted] = useState(() => {
    const saved = localStorage.getItem('cookiesAccepted');
    return saved !== null ? JSON.parse(saved) : null;
  });

  const handleCookieChoice = (choice) => {
    setCookiesAccepted(choice);
    localStorage.setItem('cookiesAccepted', JSON.stringify(choice));
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7FAFC]">
      <ScrollToTop />
      <Header />
      <main className="flex-grow">
        <LocalizedRoutes />
      </main>
      <Footer />
      {cookiesAccepted === null && (
        <CookieBanner onAccept={() => handleCookieChoice(true)} onReject={() => handleCookieChoice(false)} />
      )}
    </div>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
